/**
 * The campaign MAP PANEL (Big Phase 1.5 UI) — the player-facing map surface used
 * both as the inline mini-map on the play screen and inside the full-screen
 * viewer. Owns the fetch (`api.getSpatialMap`), the Z-level / tier LIBRARY
 * switcher (town above you / the floor you're on / the continent), the zoom/pan
 * {@link MapViewport}, a legend, and the loading / empty / error states.
 *
 * Fog of war is the SERVER's job: this only ever renders what the read returned
 * (undiscovered floors never appear in `availableZLevels`, unfound secrets never
 * carry a marker). A read failure degrades to a quiet "map unavailable" — it must
 * never break the play view.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { GetSpatialMapResponse, HierarchyLevel } from "@rufus/shared-types";

import { MapViewport } from "./MapViewport.js";

/** The port this panel needs — just the map read (a slice of the api client). */
export interface SpatialMapApi {
  getSpatialMap(campaignId: string, z?: number): Promise<GetSpatialMapResponse>;
}

export interface CampaignMapProps {
  readonly api: SpatialMapApi;
  readonly campaignId: string;
  /** `inline` = compact mini-map; `full` = the expanded viewer (more chrome). */
  readonly variant: "inline" | "full";
  /** A monotonically-changing token that forces a refetch (e.g. after a turn). */
  readonly refreshKey?: number;
}

/** Human label for a hierarchy tier, used by the library switcher. */
const TIER_LABEL: Readonly<Record<HierarchyLevel, string>> = {
  micro: "Dungeon / interior",
  meso: "Town / local area",
  macro: "Region / continent",
};

/** A player-facing name for a Z-level relative to the party's current floor. */
function zLabel(z: number, currentZ: number | undefined, availableZLevels: readonly number[]): string {
  // z is stored in FEET (mapgen-consolidation C2), so floors are NOT ±1 apart — they
  // are FLOOR_HEIGHT_FT (e.g. 20 ft) apart. Label by POSITION in the ascending list
  // of discovered levels relative to the party's level, NOT by the raw feet delta
  // (which previously said "20 floors down" for one floor). availableZLevels is
  // sorted ascending; the floor delta is the index difference.
  if (currentZ === undefined) return `Level ${z}`;
  if (z === currentZ) return "This floor";
  const sorted = [...availableZLevels].sort((a, b) => a - b);
  const zi = sorted.indexOf(z);
  const ci = sorted.indexOf(currentZ);
  if (zi === -1 || ci === -1) {
    // Fallback (a level not in the list): sign-only, no fabricated magnitude.
    return z > currentZ ? "A floor above" : "A floor below";
  }
  const delta = zi - ci; // higher z = up = later in the ascending list
  if (delta === 1) return "The floor above";
  if (delta === -1) return "The floor below";
  return delta > 0 ? `${delta} floors up` : `${Math.abs(delta)} floors down`;
}

/**
 * The INLINE mini-map's fixed, compact height (px). Small enough to glance at
 * beside the journal without pushing the text off-screen; the full-screen viewer
 * (below) is the "see it big" surface. Chosen as a readable thumbnail band.
 */
const INLINE_MAP_HEIGHT_PX = 260;

/**
 * Sizing for the outer `campaign-map` wrapper. There is no app stylesheet, so
 * the layout bound lives here as an inline style keyed on variant: the inline
 * mini-map is a block the viewport box sits inside; the full viewer fills the
 * modal overlay (column flex so the viewport can grow and the legend sits
 * beneath). This is the fix for the inline map rendering at full-column size.
 */
function containerStyle(variant: "inline" | "full"): React.CSSProperties {
  return variant === "full"
    ? { display: "flex", flexDirection: "column", width: "100%", height: "100%" }
    : { width: "100%" };
}

/**
 * Sizing for the box WRAPPING the viewport (the viewport surface is height:100%
 * and needs a bounded parent). Inline → a fixed compact height so the SVG
 * letterboxes a small map; full → flex:1 so it grows to fill the overlay.
 */
function viewportBoxStyle(variant: "inline" | "full"): React.CSSProperties {
  return variant === "full"
    ? { flex: 1, minHeight: 0, width: "100%" }
    : { height: INLINE_MAP_HEIGHT_PX, width: "100%" };
}

export function CampaignMap({ api, campaignId, variant, refreshKey = 0 }: CampaignMapProps): JSX.Element {
  const [data, setData] = useState<GetSpatialMapResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  // The Z-level the player has selected to view; undefined = follow the party.
  const [selectedZ, setSelectedZ] = useState<number | undefined>(undefined);

  // Guard against a resolve after unmount / campaign swap (same discipline as
  // the journal hydrate).
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  // LATEST-WINS request sequencing: `refreshKey` bumps on every committed turn
  // and a fast Z-switch fires back-to-back reads, so a slower EARLIER response
  // can resolve after a newer one. Each request takes a monotonic id; a
  // resolution is applied only when it is still the most recent in flight, so an
  // out-of-order response can never overwrite a newer slice with a stale one.
  const reqSeqRef = useRef(0);

  const load = useCallback(
    (z: number | undefined) => {
      const reqId = ++reqSeqRef.current;
      setStatus((s) => (data ? s : "loading"));
      api
        .getSpatialMap(campaignId, z)
        .then((res) => {
          if (!liveRef.current || reqId !== reqSeqRef.current) return; // stale / unmounted
          setData(res);
          setStatus("ready");
        })
        .catch(() => {
          if (!liveRef.current || reqId !== reqSeqRef.current) return;
          // A 404 (new campaign with no map yet / non-member) or transient read
          // failure → quiet unavailable state; never throws into the play view.
          setStatus("error");
        });
    },
    [api, campaignId, data],
  );

  // (Re)load on campaign change, Z selection, or an external refresh token.
  useEffect(() => {
    load(selectedZ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, selectedZ, refreshKey]);

  if (status === "loading" && !data) {
    return <div className={`campaign-map campaign-map-${variant}`} data-testid="map-loading">Loading map…</div>;
  }
  if (status === "error" || !data || data.map.nodes.length === 0) {
    return (
      <div className={`campaign-map campaign-map-${variant}`} data-testid="map-unavailable">
        <p className="map-empty">No map to show yet — explore to reveal the area.</p>
      </div>
    );
  }

  const { map, currentNodeId, currentZ, availableZLevels, z, tier } = data;

  return (
    <div className={`campaign-map campaign-map-${variant}`} data-testid="campaign-map" style={containerStyle(variant)}>
      {/* The map LIBRARY switcher — browse discovered floors/tiers. Only shown
          when there's more than one discovered level (or in the full viewer). */}
      {(availableZLevels.length > 1 || variant === "full") && (
        <div className="map-library" role="group" aria-label="Map levels">
          <span className="map-tier-label" data-testid="map-tier">{TIER_LABEL[tier]}</span>
          <label className="map-level-select">
            <span className="sr-only">Choose a map level</span>
            <select
              data-testid="map-level-select"
              value={z}
              onChange={(e) => setSelectedZ(Number(e.target.value))}
            >
              {availableZLevels.map((lvl) => (
                <option key={lvl} value={lvl}>
                  {zLabel(lvl, currentZ, availableZLevels)}
                </option>
              ))}
            </select>
          </label>
          {selectedZ !== undefined && selectedZ !== currentZ && currentZ !== undefined ? (
            <button type="button" data-testid="map-follow-party" onClick={() => setSelectedZ(undefined)}>
              Back to the party
            </button>
          ) : null}
        </div>
      )}

      {/* The viewport's surface is height:100%, so it needs a bounded parent to
          size against. The INLINE mini-map gets a fixed, compact box (a
          glanceable map beside the journal); the FULL viewer flexes to fill the
          overlay. Without this bound the SVG expanded to the full column width
          and scaled a 2-node map huge, pushing the journal off-screen. */}
      <div style={viewportBoxStyle(variant)} data-testid="map-viewport-box">
        <MapViewport
          map={map}
          currentNodeId={currentNodeId}
          ariaLabel={`Map of ${TIER_LABEL[tier]}`}
        />
      </div>

      {variant === "full" ? (
        <div className="map-legend" data-testid="map-legend" aria-label="Map legend">
          <span>⚠ trap (found)</span>
          <span>✦ secret (found)</span>
          <span>▮ closed door</span>
          <span>▯ open door</span>
          <span className="map-legend-here">● you are here</span>
        </div>
      ) : null}
    </div>
  );
}
